import React, { useEffect } from 'react';
import { useTable, useBlockLayout } from 'react-table';
import {
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    Paper,
    TextField,
} from '@mui/material';

import { transformToHierarchical } from './KPIUtils';

import { useDataSetters, useDataState } from '../../../../DataContext';
import { column } from 'mathjs';

import './KPIStyles.css';

const EditableCell = ({
    value: initialValue,
    row: { index }, // This index is now the vineyard index
    column: { id },
    updateData,
    vineyardId,
    blockId, // New prop to indicate which block this cell corresponds to
  }) => {
    const [value, setValue] = React.useState(initialValue);

    const onChange = (e) => {
    setValue(e.target.value);
    };

    const onBlur = () => {
    // When the user leaves the cell, we update the data with the new value
    // We now pass both the vineyardId and blockId to the updateData function
    updateData(vineyardId, blockId, id, value === 'no_data' ? value : parseFloat(value) || 0);
    };

    React.useEffect(() => {
        setValue(initialValue);
    }, [initialValue]);

    return (
    <TextField
        fullWidth
        value={value}
        onChange={onChange}
        onBlur={onBlur}
        inputProps={{
        style: {
            textAlign: 'center',
            padding: 5,
            margin: 'auto 0',
            lineHeight: 'normal',
            height: '100%',
        },
        }}
    />
    );
};
  
    // const transformToHierarchical = (data) => {
    //     return data.map(vineyard => {
    //     const children = vineyard.children.map(child => ({
    //         block_id: child.block_id,
    //         cost: child.cost === "no_data" ? 0 : parseFloat(child.cost),
    //         revenue: child.revenue === "no_data" ? 0 : parseFloat(child.revenue),
    //         yield: child.yield === "no_data" ? 0 : parseFloat(child.yield),
    //     }));
        
    //     const cost = children.reduce((acc, child) => acc + (isNaN(parseFloat(child.cost)) ? 0 : parseFloat(child.cost)), 0);
    //     const revenue = children.reduce((acc, child) => acc + (isNaN(parseFloat(child.revenue)) ? 0 : parseFloat(child.revenue)), 0);
    //     const yieldVal = children.reduce((acc, child) => acc + (isNaN(parseFloat(child.yield)) ? 0 : parseFloat(child.yield)), 0);
    
    //     return {
    //         vineyard_id: vineyard.vineyard_id,
    //         cost: cost,
    //         revenue: revenue,
    //         yield: yieldVal,
    //         children: children,
    //     };
    //     });
    // };

  const EditableTable = ({ data, setData }) => {

    const { 
        kpiSettingsTable,
        kpiUserInputs,
    } = useDataState();

    const { 
        setKpiSettingsTable,
        setKpiUserInputs,
     } = useDataSetters();
  
    const updateData = (vineyardId, blockId, columnId, value) => {
        
        setKpiSettingsTable((oldData) => {
            
            if (oldData.length === 0) {
                return [];
            }
    
            const newData = oldData.map((vineyard) => {
                
                if (vineyard.vineyard_id === vineyardId) {
                    // Found the correct vineyard
                    const updatedVineyard = { ...vineyard };
                    if (blockId != null) {
                        // Update block data
                        updatedVineyard.children = vineyard.children.map((block) => {
                            if (block.block_id === blockId) {
                                return { ...block, [columnId]: value === 'no_data' ? value : parseFloat(value) || 0 };
                            }
                            return block;
                        });
                    } else {
                        // Update vineyard data
                        updatedVineyard[columnId] = value === 'no_data' ? value : parseFloat(value) || 0;
                    }
                    return updatedVineyard;
                }
                return vineyard;
            });
            
            return newData;
        });
    };
    

    const hierarchicalData = React.useMemo(() => transformToHierarchical(data), [data]);

    useEffect(() => {
        setKpiUserInputs(hierarchicalData);
    }, [hierarchicalData, setKpiUserInputs]);

    const columns = React.useMemo(() => [
        {
            Header: 'Vineyard: Block',
            accessor: (row) => row.vineyard_id, // Just a placeholder to satisfy the accessor requirement
            Cell: ({ row }) => {
              const isVineyardTotalRow = row.original.hasOwnProperty('yield'); // Checking for a property that's only on vineyard total rows
              return isVineyardTotalRow ? (
                <span>{row.original.vineyard_id}</span>
              ) : (
                <span>{`: ${row.original.block_id}`}</span>
              );
            },
            width: 75,
          },
        {
            Header: 'Yield (tons)',
            accessor: 'yield',
            Cell: props => <EditableCell {...props} updateData={updateData} />,
            width: 125,
        },
        {
            Header: 'Revenue ($)',
            accessor: 'revenue',
            Cell: props => <EditableCell {...props} updateData={updateData} />,
            width: 125,
        },
        {
            Header: 'Cost ($)',
            accessor: 'cost',
            Cell: props => <EditableCell {...props} updateData={updateData} />,
            width: 125,
        },
        {
            Header: 'Weak Canopy Threshold',
            accessor: 'canopy_threshold',
            Cell: props => <EditableCell {...props} updateData={updateData} />,
            width: 125,
        },
        {
            Header: 'Weak Fruit Threshold',
            accessor: 'yield_threshold',
            Cell: props => <EditableCell {...props} updateData={updateData} />,
            width: 125,
        }
    ], []);

    const {
        getTableProps,
        getTableBodyProps,
        headerGroups,
        rows,
        prepareRow,
      } = useTable({ columns, data: hierarchicalData }, useBlockLayout);    

      return (
        <TableContainer component={Paper}>
            <Table {...getTableProps()} >
                <TableHead>
                    {headerGroups.map(headerGroup => (
                        <TableRow 
                            {...headerGroup.getHeaderGroupProps()}
                            style={{ tableLayout: 'fixed'}}
                        >
                            {headerGroup.headers.map(column => (
                                <TableCell
                                    {...column.getHeaderProps()}
                                    style={{
                                        fontWeight: 'bold',
                                        textAlign: 'center',
                                        verticalAlign: 'middle',
                                        padding: '6px 6px',
                                        width: column.width
                                    }}
                                >
                                    {column.render('Header')}
                                </TableCell>
                            ))}
                        </TableRow>
                    ))}
                </TableHead>
                <TableBody {...getTableBodyProps()}>
                    {hierarchicalData.map((vineyard, vIndex) => (
                        <React.Fragment key={`vineyard-${vineyard.vineyard_id}`}>
                        {/* Vineyard Row */}
                        <TableRow>
                            {/* First cell for the vineyard ID */}
                            <TableCell style={{
                                fontWeight: 'bold',
                                textAlign: 'center',
                                verticalAlign: 'middle',
                                padding: '6px 6px',
                            }}>
                            {vineyard.vineyard_id}
                            </TableCell>
                            {/* Other cells for totals */}
                            {columns.slice(1).map((column) => {
                            // Determine what to display based on the column id
                            let content;
                            switch (column.accessor) {
                                case 'yield':
                                content = `${vineyard.yield}`;
                                break;
                                case 'cost':
                                content = `${vineyard.cost}`;
                                break;
                                case 'revenue':
                                content = `${vineyard.revenue}`;
                                break;
                                default:
                                content = '--'; // If for some reason the column doesn't match
                            }
                            return (
                                <TableCell 
                                    key={`vineyard-total-${vineyard.vineyard_id}-${column.id}`}
                                    style={{
                                        fontWeight: 'bold',
                                        textAlign: 'center',
                                        verticalAlign: 'middle',
                                        padding: '6px 6px',
                                        width: column.width // apply width from column configuration
                                    }}
                                >
                                {content}
                                </TableCell>
                            );
                            })}
                        </TableRow>
                    {/* Block Rows */}
                    {vineyard.children.map((block, bIndex) => (
                    <TableRow key={`block-${block.block_id}`}>
                    {columns.map((column, colIndex) => (
                        <TableCell 
                            key={`cell-${block.block_id}-${colIndex}`} 
                            style={{ 
                                padding: '6px',
                                textAlign: 'center',
                                verticalAlign: 'middle',
                                width: colIndex === 0 ? '175px' : column.width
                            }}>
                        {/* Static cell for Block ID */}
                        {colIndex === 0 ? (
                            <span>{block.block_id}</span>
                        ) : (
                            // Editable cells for the other columns
                            <EditableCell
                            value={ block[column.accessor] }
                            row={{ index: vIndex }}
                            column={{ id: column.accessor }}
                            updateData={ updateData }
                            vineyardId={ vineyard.vineyard_id }
                            blockId={ block.block_id }
                            />
                        )}
                        </TableCell>
                    ))}
                    </TableRow>
                ))}
                </React.Fragment>
            ))}
            </TableBody>
      </Table>
    </TableContainer>
  );
};

export default EditableTable;